import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Select from '../components/Select';
import ItemCard from '../components/ItemCard';
import ModalConfirm from '../components/ModalConfirm';
import Modal from '../components/ui/Modal';
import Field, { FormRow } from '../components/ui/Field';
import Estado from '../components/ui/Estado';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { toast } from '../components/Toast';
import { fmtBRL, fmtData, hoje } from '../utils/format';

const TIPOS = [
  { value: 'TRATOR', label: 'Trator' },
  { value: 'DRONE', label: 'Drone' },
  { value: 'PULVERIZADOR_AUTOPROPELIDO', label: 'Pulverizador autopropelido' },
  { value: 'COLHEITADEIRA', label: 'Colheitadeira' },
  { value: 'PLANTADEIRA', label: 'Plantadeira' },
  { value: 'IMPLEMENTO', label: 'Implemento em geral' },
];

function rotuloTipo(tipo) {
  return TIPOS.find((t) => t.value === tipo)?.label || tipo || '—';
}

const MAQ_VAZIO = { tipo: 'TRATOR', ano: '', nome: '', descricao: '' };
const MAN_VAZIO = {
  maquinarioId: '',
  dataManutencao: hoje(),
  horimetro: '',
  custo: '',
  mecanico: '',
  descricao: '',
  proximaManutencaoData: '',
  proximaManutencaoHorimetro: '',
};

function payloadMaq(form) {
  return {
    tipo: form.tipo,
    ano: form.ano ? parseInt(form.ano, 10) : null,
    nome: form.nome.trim(),
    descricao: form.descricao?.trim() || null,
  };
}

function payloadMan(form) {
  return {
    maquinarioId: parseInt(form.maquinarioId, 10),
    dataManutencao: form.dataManutencao,
    horimetro: form.horimetro !== '' ? parseFloat(form.horimetro) : null,
    custo: form.custo !== '' ? parseFloat(form.custo) : null,
    mecanico: form.mecanico?.trim() || null,
    descricao: form.descricao?.trim() || null,
    proximaManutencaoData: form.proximaManutencaoData || null,
    proximaManutencaoHorimetro:
      form.proximaManutencaoHorimetro !== ''
        ? parseFloat(form.proximaManutencaoHorimetro)
        : null,
  };
}

export default function Maquinario() {
  const [aba, setAba] = useState('equipamentos');
  const [maquinas, setMaquinas] = useState([]);
  const [manutencoes, setManutencoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');

  const [formMaq, setFormMaq] = useState({ ...MAQ_VAZIO });
  const [formMan, setFormMan] = useState({ ...MAN_VAZIO });
  const [criandoMaq, setCriandoMaq] = useState(false);
  const [criandoMan, setCriandoMan] = useState(false);
  const [editMaq, setEditMaq] = useState(null);
  const [editMan, setEditMan] = useState(null);
  const [erroModal, setErroModal] = useState('');
  const [excluindoMaq, setExcluindoMaq] = useState(null);
  const [excluindoMan, setExcluindoMan] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [expandidaId, setExpandidaId] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const [mqs, mans] = await Promise.all([
        api.getMaquinarios(),
        api.getManutencoesMaquinario(),
      ]);
      setMaquinas(mqs || []);
      setManutencoes(mans || []);
    } catch (err) {
      setErroCarga(err.message || 'Não foi possível carregar o maquinário.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarMaq = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.criarMaquinario(payloadMaq(formMaq));
      setFormMaq({ ...MAQ_VAZIO });
      setCriandoMaq(false);
      toast('Maquinário cadastrado.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const salvarEditMaq = async (e) => {
    e?.preventDefault();
    setErroModal('');
    try {
      await api.editarMaquinario(editMaq.id, payloadMaq(editMaq));
      setEditMaq(null);
      toast('Maquinário atualizado.', 'success');
      carregar();
    } catch (err) {
      setErroModal(err.message);
    }
  };

  const salvarMan = async (e) => {
    e.preventDefault();
    if (!formMan.maquinarioId) {
      toast('Escolha o maquinário.', 'error');
      return;
    }
    setSalvando(true);
    try {
      await api.criarManutencaoMaquinario(payloadMan(formMan));
      setFormMan({ ...MAN_VAZIO });
      setCriandoMan(false);
      toast('Manutenção registrada.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const salvarEditMan = async (e) => {
    e?.preventDefault();
    setErroModal('');
    try {
      await api.editarManutencaoMaquinario(editMan.id, payloadMan(editMan));
      setEditMan(null);
      toast('Manutenção atualizada.', 'success');
      carregar();
    } catch (err) {
      setErroModal(err.message);
    }
  };

  const abrirEditMan = (m) => {
    setErroModal('');
    setEditMan({
      id: m.id,
      maquinarioId: String(m.maquinario?.id || ''),
      dataManutencao: (m.dataManutencao || '').slice(0, 10),
      horimetro: m.horimetro != null ? String(m.horimetro) : '',
      custo: m.custo != null ? String(m.custo) : '',
      mecanico: m.mecanico || '',
      descricao: m.descricao || '',
      proximaManutencaoData: m.proximaManutencaoData
        ? String(m.proximaManutencaoData).slice(0, 10)
        : '',
      proximaManutencaoHorimetro:
        m.proximaManutencaoHorimetro != null ? String(m.proximaManutencaoHorimetro) : '',
    });
  };

  const formManutencao = (form, setForm, onSubmit, rodape) => (
    <form onSubmit={onSubmit}>
      <FormRow>
        <Field label="Maquinário" obrigatorio largura="cheia">
          <Select
            value={form.maquinarioId}
            onChange={(v) => setForm((f) => ({ ...f, maquinarioId: v }))}
            options={maquinas.map((m) => ({
              value: String(m.id),
              label: `${m.nome} (${rotuloTipo(m.tipo)})`,
            }))}
            placeholder={maquinas.length ? 'Selecione' : 'Cadastre um maquinário antes'}
            disabled={maquinas.length === 0}
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Data da manutenção" obrigatorio>
          <input
            type="date"
            value={form.dataManutencao}
            onChange={(e) => setForm((f) => ({ ...f, dataManutencao: e.target.value }))}
            required
          />
        </Field>
        <Field label="Horímetro (horas)">
          <input
            type="number"
            step="0.1"
            min="0"
            value={form.horimetro}
            onChange={(e) => setForm((f) => ({ ...f, horimetro: e.target.value }))}
            placeholder="Opcional"
          />
        </Field>
        <Field label="Custo (R$)">
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={form.custo}
            onChange={(e) => setForm((f) => ({ ...f, custo: e.target.value }))}
            placeholder="Opcional"
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Mecânico" largura="cheia">
          <input
            value={form.mecanico}
            onChange={(e) => setForm((f) => ({ ...f, mecanico: e.target.value }))}
            placeholder="Opcional"
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field
          label="Descrição"
          hint="Peças trocadas ou serviços feitos"
          largura="cheia"
        >
          <input
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            placeholder="Opcional"
            maxLength={1000}
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Próxima manutenção (data)">
          <input
            type="date"
            value={form.proximaManutencaoData}
            onChange={(e) => setForm((f) => ({ ...f, proximaManutencaoData: e.target.value }))}
          />
        </Field>
        <Field label="Próxima manutenção (horímetro)">
          <input
            type="number"
            step="0.1"
            min="0"
            value={form.proximaManutencaoHorimetro}
            onChange={(e) => setForm((f) => ({ ...f, proximaManutencaoHorimetro: e.target.value }))}
            placeholder="Opcional"
          />
        </Field>
      </FormRow>
      {rodape}
    </form>
  );

  const camposMaquinario = (dados, aplicar) => (
    <>
      <FormRow>
        <Field label="Tipo" obrigatorio>
          <Select
            value={dados.tipo}
            onChange={(v) => aplicar((f) => ({ ...f, tipo: v }))}
            options={TIPOS}
          />
        </Field>
        <Field label="Ano">
          <input
            type="number"
            min="1950"
            max="2100"
            value={dados.ano}
            onChange={(e) => aplicar((f) => ({ ...f, ano: e.target.value }))}
            placeholder="Ex: 2018"
          />
        </Field>
        <Field label="Nome" obrigatorio>
          <input
            value={dados.nome}
            onChange={(e) => aplicar((f) => ({ ...f, nome: e.target.value }))}
            required
            placeholder="Ex: John Deere 6110J"
          />
        </Field>
      </FormRow>
      <Field label="Descrição">
        <input
          value={dados.descricao}
          onChange={(e) => aplicar((f) => ({ ...f, descricao: e.target.value }))}
          placeholder="Opcional"
          maxLength={500}
        />
      </Field>
    </>
  );

  // A ação principal muda com a aba: em Manutenções ela também depende de já
  // existir algum equipamento, senão o formulário abriria com o select travado.
  const acaoDoTopo = aba === 'equipamentos'
    ? { label: 'Novo maquinário', icone: 'adicionar', onClick: () => setCriandoMaq(true) }
    : maquinas.length > 0
      ? { label: 'Registrar manutenção', icone: 'adicionar', onClick: () => setCriandoMan(true) }
      : undefined;

  return (
    <div>
      <PageHeader
        titulo="Maquinário"
        icone="maquinario"
        descricao="Tratores, drones e implementos da fazenda, com o histórico de manutenções."
        acao={acaoDoTopo}
      />

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'equipamentos'}
          className={aba === 'equipamentos' ? 'tab active' : 'tab'}
          onClick={() => setAba('equipamentos')}
        >
          Equipamentos
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'manutencoes'}
          className={aba === 'manutencoes' ? 'tab active' : 'tab'}
          onClick={() => setAba('manutencoes')}
        >
          Manutenções
        </button>
      </div>

      {aba === 'equipamentos' && (
          <div className="card">
            <div className="secao-titulo">
              <h2>Maquinário cadastrado</h2>
              {maquinas.length > 0 && <span className="contador">{maquinas.length}</span>}
            </div>
            <Estado carregando={carregando} erro={erroCarga} aoTentarNovamente={carregar} itens={3}>
              {maquinas.length === 0 ? (
                <EmptyState
                  compacto
                  icone="maquinario"
                  titulo="Nenhum maquinário"
                  descricao="Cadastre o primeiro equipamento para começar a acompanhar as manutenções."
                  acao={{ label: 'Novo maquinário', onClick: () => setCriandoMaq(true) }}
                />
              ) : maquinas.map((m) => (
                <ItemCard
                  key={m.id}
                  onEdit={() => {
                    setErroModal('');
                    setEditMaq({
                      id: m.id,
                      tipo: m.tipo,
                      ano: m.ano != null ? String(m.ano) : '',
                      nome: m.nome || '',
                      descricao: m.descricao || '',
                    });
                  }}
                  onDelete={() => setExcluindoMaq(m)}
                >
                  <div className="item-conteudo">
                    <div className="item-titulo">{m.nome}</div>
                    <div className="item-meta">
                      {rotuloTipo(m.tipo)}
                      {m.ano ? ` · ${m.ano}` : ''}
                      {m.descricao ? ` · ${m.descricao}` : ''}
                    </div>
                  </div>
                </ItemCard>
              ))}
            </Estado>
          </div>
      )}

      {aba === 'manutencoes' && (
          <div className="card">
            <div className="secao-titulo">
              <h2>Histórico</h2>
              {manutencoes.length > 0 && <span className="contador">{manutencoes.length}</span>}
            </div>
            <Estado carregando={carregando} erro={erroCarga} aoTentarNovamente={carregar} itens={3}>
              {manutencoes.length === 0 ? (
                <EmptyState
                  compacto
                  icone="manutencao"
                  titulo="Nenhuma manutenção"
                  descricao={maquinas.length === 0
                    ? 'Cadastre um equipamento antes de registrar manutenções.'
                    : 'Registre o que foi feito e, se quiser, a data da próxima.'}
                  acao={maquinas.length === 0
                    ? { label: 'Novo maquinário', onClick: () => { setAba('equipamentos'); setCriandoMaq(true); } }
                    : { label: 'Registrar manutenção', onClick: () => setCriandoMan(true) }}
                />
              ) : (
                <div className="lista-cards">
                  {manutencoes.map((m) => {
                const aberta = expandidaId === m.id;
                return (
                  <div key={m.id} className="item-expansivel">
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={aberta}
                      onClick={() => setExpandidaId(aberta ? null : m.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandidaId(aberta ? null : m.id);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <ItemCard
                        onEdit={() => abrirEditMan(m)}
                        onDelete={() => setExcluindoMan(m)}
                      >
                        <div className="item-conteudo">
                          <div className="item-titulo">{m.maquinario?.nome || 'Maquinário'}</div>
                          <div className="item-meta">
                            {fmtData(m.dataManutencao)}
                            {m.mecanico ? ` · ${m.mecanico}` : ''}
                            {m.proximaManutencaoData
                              ? ` · Próxima: ${fmtData(m.proximaManutencaoData)}`
                              : ''}
                          </div>
                        </div>
                        {m.custo != null && <div className="item-valor">{fmtBRL(m.custo)}</div>}
                        <span className="item-expandir" aria-hidden="true">
                          <Icon nome={aberta ? 'chevron-cima' : 'chevron-baixo'} tamanho={16} />
                        </span>
                      </ItemCard>
                    </div>
                    {aberta && (
                      <div className="painel-detalhe">
                        <div className="painel-detalhe__rotulo">Descrição</div>
                        <div>{m.descricao?.trim() || '—'}</div>
                        <div className="painel-detalhe__grade">
                          <div>
                            <div className="painel-detalhe__rotulo">Horímetro</div>
                            <div>{m.horimetro != null ? `${m.horimetro} h` : '—'}</div>
                          </div>
                          <div>
                            <div className="painel-detalhe__rotulo">Próx. horímetro</div>
                            <div>
                              {m.proximaManutencaoHorimetro != null
                                ? `${m.proximaManutencaoHorimetro} h`
                                : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
                  })}
                </div>
              )}
            </Estado>
          </div>
      )}

      {criandoMaq && (
        <Modal titulo="Novo maquinário" onClose={() => setCriandoMaq(false)} largura={520}>
          <form onSubmit={salvarMaq}>
            {camposMaquinario(formMaq, setFormMaq)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setCriandoMaq(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {criandoMan && (
        <Modal titulo="Registrar manutenção" onClose={() => setCriandoMan(false)} largura={560}>
          <p className="secao-sub">
            Se informar a data da próxima, o aviso aparece em Notificações a partir de 7 dias antes.
          </p>
          {formManutencao(
            formMan,
            setFormMan,
            salvarMan,
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setCriandoMan(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={salvando || maquinas.length === 0}>
                {salvando ? 'Salvando...' : 'Registrar manutenção'}
              </button>
            </div>
          )}
        </Modal>
      )}

      {editMaq && (
        <Modal titulo="Editar maquinário" onClose={() => setEditMaq(null)} largura={520}>
          {erroModal && <div className="aviso aviso--erro">{erroModal}</div>}
          <form onSubmit={salvarEditMaq}>
            {camposMaquinario(editMaq, setEditMaq)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setEditMaq(null)}>Cancelar</button>
              <button type="submit" className="btn-primary">Salvar</button>
            </div>
          </form>
        </Modal>
      )}

      {editMan && (
        <Modal titulo="Editar manutenção" onClose={() => setEditMan(null)} largura={560}>
          {erroModal && <div className="aviso aviso--erro">{erroModal}</div>}
          {formManutencao(
            editMan,
            setEditMan,
            salvarEditMan,
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setEditMan(null)}>Cancelar</button>
              <button type="submit" className="btn-primary">Salvar</button>
            </div>
          )}
        </Modal>
      )}

      {excluindoMaq && (
        <ModalConfirm
          titulo="Excluir maquinário"
          mensagem={<>Excluir <strong>{excluindoMaq.nome}</strong>? As manutenções dele também serão removidas.</>}
          onConfirm={async () => {
            try {
              await api.excluirMaquinario(excluindoMaq.id);
              setExcluindoMaq(null);
              toast('Maquinário removido.', 'success');
              carregar();
            } catch (err) {
              setExcluindoMaq(null);
              toast(err.message, 'error');
            }
          }}
          onCancel={() => setExcluindoMaq(null)}
        />
      )}

      {excluindoMan && (
        <ModalConfirm
          titulo="Excluir manutenção"
          mensagem={<>Excluir manutenção de <strong>{excluindoMan.maquinario?.nome}</strong> em {fmtData(excluindoMan.dataManutencao)}?</>}
          onConfirm={async () => {
            try {
              await api.excluirManutencaoMaquinario(excluindoMan.id);
              setExcluindoMan(null);
              toast('Manutenção removida.', 'success');
              carregar();
            } catch (err) {
              setExcluindoMan(null);
              toast(err.message, 'error');
            }
          }}
          onCancel={() => setExcluindoMan(null)}
        />
      )}
    </div>
  );
}
